// FLAGS: health-data
// Medical check-in themes, scores and verbatim answers for one client.
import React from "react";
import { useTranslation } from "react-i18next";
import { ModelsTheme } from "@src/api/v1/data-contracts";
import ScrollInfoRow from "@src/components/table/ScrollInfoRow";
import { useScoredQuestionLists } from "@src/hooks/useScoredQuestionLists";
import { useClientMedicalCheckinOverviewQuery } from "@src/queries/clients";
import { isEmptyQueryResult } from "@src/queries/utils";
import AnswerCell from "./table/AnswerCell";
import { TypographyWithIcon } from "./utils/TypographyWithIcon";

interface CheckinAnswersTableProps {
  clientId: string;
}

const CheckinAnswersTable = ({ clientId }: CheckinAnswersTableProps) => {
  const { data: medicalCheckin } =
    useClientMedicalCheckinOverviewQuery(clientId);
  const { t } = useTranslation();
  const { scoredQuestionLists, themesWithQuestions } =
    useScoredQuestionLists(medicalCheckin);

  if (!medicalCheckin || isEmptyQueryResult(medicalCheckin)) {
    return null;
  }

  return (
    <div className="rounded-lg bg-white shadow-md p-6">
      <div className="overflow-x-auto">
        <table className="min-w-[650px] w-full" aria-label="simple table">
          <thead>
            <ScrollInfoRow
              date={scoredQuestionLists[0].createdAt}
              scoreColumnName="Common.Score"
              score={scoredQuestionLists[0].score}
            />
          </thead>
          <tbody>
            {themesWithQuestions.map((theme: ModelsTheme) => (
              <React.Fragment key={theme.key}>
                <tr className="hover:bg-gray-50 transition-colors">
                  <th className="p-4 text-left font-normal border-0 border-b-2 border-solid border-gray-200">
                    <TypographyWithIcon
                      iconLigature="group"
                      outlined
                      variant="subtitle2"
                      boxClass="mb-0"
                    >
                      {t(`Themes.${theme.key}.Name`)}
                    </TypographyWithIcon>
                  </th>
                  {theme.questionLists?.map((questionList) => (
                    <td
                      className="p-4 text-right text-sm font-bold border-0 border-b-2 border-solid border-gray-200"
                      key={questionList.key}
                    >
                      {questionList.score.toFixed(1)}
                    </td>
                  ))}
                </tr>
                {theme.questionLists?.flatMap((questionList) =>
                  questionList.questions?.map((question) => (
                    <tr
                      key={question.key}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="p-4 text-left font-normal border-0 border-b border-solid border-gray-200">
                        <span className="text-sm opacity-70">
                          {question.text}
                        </span>
                      </td>
                      <td className="p-4 text-right border-0 border-b border-solid border-gray-200">
                        <AnswerCell answers={question.answers} />
                      </td>
                    </tr>
                  )),
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CheckinAnswersTable;
